/** The Kaufanfrage: a focused, three-step overlay on the product page.
 *
 *   1. Wunschtermin - the calendar picker (skippable)
 *   2. Ihre Daten   - name, email, phone, message
 *   3. Absenden     - summary + captcha + send
 *
 * Everything else dims away; the instrument stays pinned in the header so the
 * visitor never loses sight of what they are asking about. Booking behaviour
 * follows the admin's booking_mode with the same graceful CalDAV fallback as
 * the contact form.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  api,
  mediaUrl,
  type Captcha,
  type Product,
  type SubmitResult,
} from '../lib/api'
import { useLocale, useSiteConfig } from '../lib/hooks'
import { fill, t } from '../lib/i18n'
import { VisitorScheduler, formatShort } from './Scheduler'

const fieldClass =
  'w-full rounded-[2px] border border-line bg-white px-4 py-3 font-sans text-[17px] text-ink ' +
  'placeholder:text-slate/70 outline-none transition-[border-color,box-shadow] focus:border-amber focus:ring-2 focus:ring-amber/30'

const labelClass = 'mb-1.5 block font-sans text-[14px] font-semibold text-slate'

const primaryButtonClass =
  'inline-flex cursor-pointer items-center rounded-[2px] bg-ink px-8 py-3.5 font-sans text-[15px] font-semibold leading-none text-paper transition-colors hover:bg-walnut'

export default function BuyRequestWizard({
  product,
  onClose,
}: {
  product: Product
  onClose: () => void
}) {
  const locale = useLocale()
  const strings = t(locale)
  const w = strings.wizard
  const config = useSiteConfig()

  const [step, setStep] = useState(0)
  const [fields, setFields] = useState({ name: '', email: '', phone: '', message: '' })
  const [proposed, setProposed] = useState<string[]>([])
  const [slot, setSlot] = useState<string | null>(null)
  const [forceRequest, setForceRequest] = useState(false)
  const [calendarDown, setCalendarDown] = useState(false)
  const [captcha, setCaptcha] = useState<Captcha | null>(null)
  const [captchaAnswer, setCaptchaAnswer] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)

  const configuredMode = config?.booking_mode ?? 'request'
  const mode: 'request' | 'live' =
    configuredMode === 'request' || forceRequest ? 'request' : 'live'

  const loadCaptcha = () => {
    api.get<Captcha>('/api/captcha').then(setCaptcha).catch(() => setCaptcha(null))
  }
  useEffect(loadCaptcha, [])

  // Lock the page behind the overlay; ESC closes.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [onClose])

  const emailValid = /.+@.+\..+/.test(fields.email)
  const primaryImage = product.images[0]

  function next() {
    setError('')
    if (step === 1 && (fields.name.trim().length < 2 || !emailValid)) {
      setError(w.contactInvalid)
      return
    }
    setStep(step + 1)
  }

  async function submit() {
    setSubmitting(true)
    setError('')
    const shared = {
      name: fields.name,
      email: fields.email,
      phone: fields.phone,
      message: fields.message,
      locale,
      captcha_id: captcha?.id ?? '',
      captcha_answer: captchaAnswer,
      website: honeypot,
      product_slug: product.slug,
    }
    try {
      let response: SubmitResult
      if (mode === 'live' && slot) {
        response = await api.post<SubmitResult>('/api/bookings', { ...shared, start: slot })
      } else {
        response = await api.post<SubmitResult>('/api/inquiries', {
          ...shared,
          subject: product.name,
          preferred_slots: proposed,
        })
      }
      setSuccess(response.message)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : strings.form.error)
      loadCaptcha()
      setCaptchaAnswer('')
    } finally {
      setSubmitting(false)
    }
  }

  const chosenTimes = useMemo(
    () => (mode === 'live' ? (slot ? [slot] : []) : proposed),
    [mode, slot, proposed],
  )

  const steps = w.steps

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 pt-[5vh] backdrop-blur-[2px] max-md:p-0 max-md:pt-0"
      role="dialog"
      aria-modal="true"
      aria-label={`${w.eyebrow}: ${product.name}`}
      data-testid="buy-wizard"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div className="w-full max-w-3xl rounded-[2px] bg-paper shadow-2xl max-md:min-h-full">
        {/* Header: the instrument stays in view the whole time. */}
        <div className="flex items-center gap-4 border-b border-line px-7 py-5 max-md:px-5">
          {primaryImage ? (
            <img
              src={mediaUrl(primaryImage.filename, 480)}
              alt=""
              className="h-[56px] w-[56px] shrink-0 rounded-[2px] border border-line object-cover"
            />
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="eyebrow m-0">{w.eyebrow}</p>
            <p className="m-0 truncate font-serif text-[24px] leading-[1.3] text-ink">
              {product.name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={w.close}
            data-testid="buy-wizard-close"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[2px] text-slate transition-colors hover:bg-paper-deep hover:text-ink"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {success ? (
          /* ---------------- Success ---------------- */
          <div className="flex flex-col items-center px-7 py-14 text-center" data-testid="form-success">
            <span className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-amber/20 text-ink">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            <p className="m-0 font-serif text-[34px] text-ink">{w.successTitle}</p>
            <p className="m-0 mt-2 max-w-[420px] font-sans text-[18px] leading-[1.6] text-slate">
              {success}
            </p>
            <button type="button" onClick={onClose} className={`mt-8 ${primaryButtonClass}`}>
              {w.close}
            </button>
          </div>
        ) : (
          <>
            {/* Stepper */}
            <div className="flex items-center justify-center gap-0 border-b border-line px-7 py-4">
              {steps.map((label, index) => (
                <div key={label} className="flex items-center">
                  {index > 0 ? (
                    <span
                      className={`mx-3 h-px w-8 max-md:w-4 ${index <= step ? 'bg-ink' : 'bg-line'}`}
                    />
                  ) : null}
                  <span className="flex items-center gap-2">
                    <span
                      className={`flex h-7 w-7 items-center justify-center rounded-full font-sans text-[13px] font-semibold ${
                        index < step
                          ? 'bg-amber/25 text-ink'
                          : index === step
                            ? 'bg-ink text-paper'
                            : 'border border-line text-slate'
                      }`}
                    >
                      {index < step ? '✓' : index + 1}
                    </span>
                    <span
                      className={`font-sans text-[14px] font-semibold max-md:hidden ${
                        index === step ? 'text-ink' : 'text-slate'
                      }`}
                    >
                      {label}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            <div className="px-7 py-6 max-md:px-5" data-testid={`buy-step-${step}`}>
              {step === 0 ? (
                <>
                  <p className="m-0 mb-5 font-sans text-[18px] leading-[1.6] text-ink">
                    {mode === 'live' ? w.terminHintLive : w.terminHint}
                  </p>
                  {calendarDown ? (
                    <p
                      className="mb-4 rounded-[2px] border border-line bg-paper-deep px-4 py-2.5 font-sans text-[16px] text-ink"
                      data-testid="calendar-warning"
                    >
                      {strings.form.calendarUnavailable}
                    </p>
                  ) : null}
                  {config ? (
                    <VisitorScheduler
                      key={mode}
                      config={config}
                      locale={locale}
                      mode={mode}
                      proposed={proposed}
                      onProposed={setProposed}
                      slot={slot}
                      onSlot={setSlot}
                      onCalendarUnavailable={() => {
                        setCalendarDown(true)
                        setForceRequest(true)
                      }}
                    />
                  ) : null}
                </>
              ) : null}

              {step === 1 ? (
                <div className="mx-auto max-w-[440px]">
                  <p className="m-0 mb-5 text-center font-sans text-[18px] leading-[1.6] text-ink">
                    {w.contactHint}
                  </p>
                  {/* Honeypot - the server only flags a filled one, since
                      autofill reaches hidden fields for real people too. */}
                  <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
                    <input
                      id="hp_field"
                      name="hp_field"
                      tabIndex={-1}
                      autoComplete="off"
                      data-lpignore="true"
                      data-1p-ignore="true"
                      data-bwignore="true"
                      data-form-type="other"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="bw-name" className={labelClass}>
                        {strings.form.name} *
                      </label>
                      <input
                        id="bw-name"
                        className={fieldClass}
                        autoComplete="name"
                        value={fields.name}
                        onChange={(e) => setFields({ ...fields, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor="bw-email" className={labelClass}>
                        {strings.form.email} *
                      </label>
                      <input
                        id="bw-email"
                        type="email"
                        className={fieldClass}
                        autoComplete="email"
                        value={fields.email}
                        onChange={(e) => setFields({ ...fields, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor="bw-phone" className={labelClass}>
                        {strings.form.phone}
                      </label>
                      <input
                        id="bw-phone"
                        type="tel"
                        className={fieldClass}
                        autoComplete="tel"
                        value={fields.phone}
                        onChange={(e) => setFields({ ...fields, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label htmlFor="bw-message" className={labelClass}>
                        {w.messageLabel}
                      </label>
                      <textarea
                        id="bw-message"
                        rows={4}
                        className={fieldClass}
                        placeholder={w.messagePlaceholder}
                        value={fields.message}
                        onChange={(e) => setFields({ ...fields, message: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              {step === 2 ? (
                <div className="mx-auto max-w-[480px]">
                  <p className="m-0 mb-5 text-center font-sans text-[18px] leading-[1.6] text-ink">
                    {w.summaryHint}
                  </p>
                  <dl
                    className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 rounded-[2px] border border-line bg-white p-5 font-sans text-[16px] text-ink"
                    data-testid="buy-summary"
                  >
                    <dt className="text-slate">{w.summaryProduct}</dt>
                    <dd className="m-0">{product.name}</dd>
                    <dt className="text-slate">
                      {mode === 'live' ? w.summarySlot : w.summaryTimes}
                    </dt>
                    <dd className="m-0">
                      {chosenTimes.length
                        ? chosenTimes.map((iso) => (
                            <span key={iso} className="block">
                              {formatShort(iso, locale)}
                            </span>
                          ))
                        : w.noTimes}
                    </dd>
                    <dt className="text-slate">{w.summaryContact}</dt>
                    <dd className="m-0">
                      {fields.name}
                      <span className="block text-[15px] text-slate">
                        {fields.email}
                        {fields.phone ? ` · ${fields.phone}` : ''}
                      </span>
                    </dd>
                    {fields.message ? (
                      <>
                        <dt className="text-slate">{w.summaryMessage}</dt>
                        <dd className="m-0 text-[15px]">{fields.message}</dd>
                      </>
                    ) : null}
                  </dl>

                  <label htmlFor="bw-captcha" className={`${labelClass} mt-6 text-center`}>
                    {strings.form.captcha}
                  </label>
                  <div className="flex items-center justify-center gap-3">
                    <span
                      className="whitespace-nowrap font-serif text-[24px] leading-none text-ink"
                      data-testid="captcha-question"
                    >
                      {captcha?.question ?? '…'} =
                    </span>
                    <input
                      id="bw-captcha"
                      inputMode="numeric"
                      autoComplete="off"
                      data-testid="captcha-input"
                      className={`${fieldClass.replace('w-full ', '')} h-[52px] w-[72px] px-0 text-center`}
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                    />
                  </div>
                  <p className="mb-0 mt-2 text-center font-sans text-[14px] leading-[1.5] text-slate">
                    {strings.form.captchaHint}
                  </p>
                </div>
              ) : null}

              {error ? (
                <p
                  className="mt-5 rounded-[2px] border border-walnut bg-paper-deep px-4 py-2.5 text-center font-sans text-[16px] text-ink"
                  role="alert"
                  data-testid="form-error"
                >
                  {error}
                </p>
              ) : null}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-line px-7 py-4 max-md:px-5">
              <span className="font-sans text-[14px] text-slate">
                {fill(w.stepOf, { i: step + 1, n: steps.length })}
              </span>
              <div className="flex items-center gap-3">
                {step > 0 ? (
                  <button
                    type="button"
                    onClick={() => {
                      setError('')
                      setStep(step - 1)
                    }}
                    data-testid="buy-back"
                    className="cursor-pointer px-4 py-3 font-sans text-[15px] font-semibold text-slate transition-colors hover:text-ink"
                  >
                    ← {w.back}
                  </button>
                ) : null}
                {step === 0 && chosenTimes.length === 0 ? (
                  <button
                    type="button"
                    onClick={next}
                    data-testid="buy-skip"
                    className="cursor-pointer px-4 py-3 font-sans text-[15px] font-semibold text-slate underline underline-offset-4 transition-colors hover:text-ink"
                  >
                    {w.skip}
                  </button>
                ) : null}
                {step < 2 ? (
                  step === 0 && chosenTimes.length === 0 ? null : (
                    <button
                      type="button"
                      onClick={next}
                      data-testid="buy-next"
                      className={primaryButtonClass}
                    >
                      {w.next} →
                    </button>
                  )
                ) : (
                  <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    data-testid="buy-submit"
                    className={`${primaryButtonClass} disabled:cursor-not-allowed disabled:opacity-60`}
                  >
                    {submitting
                      ? strings.form.sending
                      : mode === 'live' && slot
                        ? w.book
                        : w.send}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
